import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProyectoService } from 'src/app/services/proyecto.service';
import { SocioService } from 'src/app/services/socio.service';
import { Productos } from 'src/app/models/producto.model';
import { CriptoService } from 'src/app/services/cripto.service';
import { DatePipe } from '@angular/common';
import Swal from 'sweetalert2';
import { RegistroRequest } from 'src/app/models/registro.model'; // Asegúrate de importar el modelo adecuado

@Component({
  selector: 'app-registro',
  templateUrl: './registro.component.html',
  styleUrls: ['./registro.component.scss'],
  providers: [DatePipe]
})
export class RegistroComponent implements OnInit {
  isSessionActive: boolean = Boolean(localStorage.getItem('token'));
  productos: Productos[] = [];
  registros: any[] = [];
  selectedCategoriaId: number | null = null;
  selectedTipoDePago: string | null = null;

  nombreCompleto: string = '';
  fechaNacimiento: string = '';

  public idSocioDesencriptado: string;

  registroForm: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private productService: ProyectoService,
    private router: Router,
    private socioService: SocioService,
    private _criptoService: CriptoService,
    private datePipe: DatePipe
  ) {
    this.idSocioDesencriptado = this._criptoService.decrypt(localStorage.getItem('idSocio'));

    this.registroForm = this.formBuilder.group({
      nombreCompleto: ['', Validators.required],
      fechaNacimiento: ['', Validators.required],
      talla: ['', Validators.required],
      categoria: ['', Validators.required],
      correoElectronico: ['', [Validators.required, Validators.email]],
      telefono: ['', Validators.required],
      tipoDePago: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.productService.getAllProducts().subscribe(
      response => {
        if (response.success) {
          this.productos = response.data;
        } else {
          console.error('Error en la respuesta:', response.messages);
        }
      },
      error => {
        console.error('Error al obtener productos:', error);
      }
    );

    if (this.isSessionActive && this.idSocioDesencriptado) {
      const idSocioNumber = parseInt(this.idSocioDesencriptado, 10);
      this.socioService.getSocio(idSocioNumber).subscribe(
        response => {
          if (response && response.data) {
            const nombre = response.data.nombreCompleto || '';
            const apellidoPat = response.data.apellidoPat || '';
            const apellidoMat = response.data.apellidoMat || '';

            this.nombreCompleto = `${nombre} ${apellidoPat} ${apellidoMat}`.trim();

            if (response.data.fechaNacimiento) {
              const fecha = new Date(response.data.fechaNacimiento);
              if (!isNaN(fecha.getTime())) {
                this.fechaNacimiento = this.datePipe.transform(fecha, 'dd/MM/yyyy') || '';
                this.registroForm.patchValue({
                  fechaNacimiento: fecha
                });
              } else {
                console.error('Fecha de nacimiento inválida:', response.data.fechaNacimiento);
              }
            } else {
              console.error('Fecha de nacimiento no proporcionada:', response.data);
            }

            this.registroForm.patchValue({
              nombreCompleto: this.nombreCompleto
            });
          }
        },
        error => {
          console.error('Error al obtener datos del socio:', error);
        }
      );
    }

    // Llamar al método getAllRegistro y almacenar los registros con mensajes de depuración adicionales
    this.productService.getAllRegistro().subscribe(
      response => {
        if (response.success) {
          this.registros = response.data;
          console.log('Registros obtenidos:', this.registros);
        } else {
          console.error('Error al obtener registros:', response.messages);
        }
      },
      error => {
        console.error('Error al obtener los registros:', error);
      }
    );
  }

  onCategoriaChange(event: any): void {
    const selectedValue = event.target.value;
    if (selectedValue) {
      this.selectedCategoriaId = parseInt(selectedValue, 10);
    } else {
      this.selectedCategoriaId = null;
    }
  }

  onTipoDePagoChange(event: any): void {
    this.selectedTipoDePago = event.target.value;
  }

  correoExiste(correo: string): boolean {
    console.log('Verificando si el correo ya existe:', correo);
    const existe = this.registros.some(registro => registro.correoElectronico === correo);
    console.log('Correo ya existe:', existe);
    return existe;
  }

  registrarse(): void {
    if (this.registroForm.valid) {
      const correo = this.registroForm.get('correoElectronico')?.value;

      if (this.correoExiste(correo)) {
        Swal.fire({
          icon: 'error',
          title: 'Correo Electrónico ya registrado',
          text: 'El correo electrónico ingresado ya está registrado. Por favor, use un correo diferente.',
        });
        return;
      }

      const nuevoRegistro = this.registroForm.value;

      this.productService.postRegistro(nuevoRegistro).subscribe(
        response => {
          if (response.success) {
            Swal.fire({
              icon: 'success',
              title: 'Registro exitoso',
              text: 'Te has registrado exitosamente.',
            }).then(() => {
              // Limpiar el formulario después del registro exitoso
              this.registroForm.reset();
              this.selectedCategoriaId = null;
              this.selectedTipoDePago = null;
              // Redirigir a la página de pago en línea si es necesario
              if (nuevoRegistro.tipoDePago === 'Linea') {
                this.router.navigate(['/pago-online', nuevoRegistro.categoria]);
              }
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error en el registro',
              text: 'Hubo un error al procesar tu registro. Intenta nuevamente.',
            });
          }
        },
        error => {
          Swal.fire({
            icon: 'error',
            title: 'Error en el registro',
            text: 'Hubo un error al procesar tu registro. Intenta nuevamente.',
          });
        }
      );
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Formulario inválido',
        text: 'Por favor, completa todos los campos obligatorios.',
      });

      this.registroForm.markAllAsTouched();
    }
  }
}
